import { COLUMNS } from 'components/Submissions/Table';
import { useApp } from 'contexts/app';
import { useDialog } from 'contexts/dialog';
import { createContext, useMemo, useState } from 'react';
import assignmentService from 'services/assignment';
import { useTableSortableHeaderCellEffectHandlers } from 'shell/components';
import { exportSubmissionsToCSV } from 'utils/helper';

const PageContext = createContext();

const handleFilters = (allData, filters, setSortedFilteredData) => {
    let res = [...allData];

    res = res.filter((submission) => {
        const searchCond = submission.data.name.toLowerCase().includes(filters.search.toLowerCase());
        const statusCond = !filters.status.length
            ? true
            : filters.status.some((status) => {
                  if (status === 'ready') {
                      return submission.ai && !submission.grading;
                  }
                  if (status === 'reviewed') {
                      return submission.ai && submission.grading;
                  }
                  return false;
              });
        return searchCond && statusCond;
    });

    setSortedFilteredData(res);
};

const PageProvider = (props) => {
    const { course, currentAssignment, submissions, setSubmissions, setBatch } = useApp();
    const { setDialog, closeDialog } = useDialog();

    const [filters, setFilters] = useState({
        open: false,
        status: [],
        search: '',
    });

    // memoize to not trigger re-render
    const extras = useMemo(() => {
        return currentAssignment;
    }, [currentAssignment]);

    // memoize to not trigger re-render
    const allSubmissions = useMemo(() => {
        return submissions;
    }, [(submissions ? submissions : []).map((s) => s.id).join('.')]);

    const {
        sortedFilteredData: sortedFilteredSubmissions,
        sortState,
        handleSetSortState,
    } = useTableSortableHeaderCellEffectHandlers(
        allSubmissions,
        COLUMNS,
        filters,
        setSubmissions,
        handleFilters,
        extras
    );

    const resetFilters = () => {
        setFilters((current) => ({
            ...current,
            status: [],
        }));
        handleSetSortState({ order: 'asc', orderBy: null });
    };

    const exportSubmissions = () => {
        return exportSubmissionsToCSV(course, currentAssignment, submissions);
    };

    const resetSubmissions = () => {
        setDialog({
            open: true,
            message: 'Erase Existing Assignment Data?',
            description:
                'If you reset, any existing submissions and associated grades/feedback will be permanently deleted.',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Continue',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                setSubmissions([]);
                setBatch([]);
                closeDialog();
                await Promise.all([
                    await assignmentService.deleteAssignmentSubmissions(currentAssignment.id),
                    await assignmentService.deleteAssignmentBatch(currentAssignment.id),
                ]);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    return (
        <PageContext.Provider
            value={{
                assignment: currentAssignment,
                submissions,
                sortedFilteredSubmissions,
                setSubmissions,
                sortState,
                handleSetSortState,
                exportSubmissions,
                resetSubmissions,
                filters,
                setFilters,
                resetFilters,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
